// Brand Comparison — side-by-side cards for BestRx / LeenRx / Future Brands
// Each card shows: Leads · Assessments · Approvals · Conversion % · Revenue · Revenue / Lead
import { useEffect, useState } from "react";
import { BarChart3, CircleDot, Users, CheckCircle2, Plug, DollarSign, TrendingUp, Activity, Sparkles } from "lucide-react";
import { API, RED, NEON, AMBER, PURPLE, fmt$, authHeader } from "./shared";

const CARDS = [
  { brand: "BestRx",  label: "BESTRX",  tagline: "Founding brand · Texas operating system", accent: RED,    Icon: Activity },
  { brand: "LeenRx",  label: "LEENRX",  tagline: "Weight & metabolic specialty",            accent: PURPLE, Icon: TrendingUp },
  { brand: "Future",  label: "FUTURE BRANDS", tagline: "+ NEW VERTICAL · Reserved slot",     accent: AMBER,  Icon: Sparkles, future: true },
];

export function BrandComparison({ token, cityFilter }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let live = true;
    const url = cityFilter
      ? `${API}/reports/brand-performance?city=${encodeURIComponent(cityFilter)}`
      : `${API}/reports/brand-performance`;
    fetch(url, { headers: authHeader(token) })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (live) setRows(d || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [token, cityFilter]);

  const totals = rows.reduce(
    (a, r) => ({
      leads: a.leads + (r.leads || 0),
      revenue_cents: a.revenue_cents + (r.revenue_cents || 0),
      assessments: a.assessments + (r.assessments || 0),
      approvals: a.approvals + (r.approvals || 0),
    }),
    { leads: 0, revenue_cents: 0, assessments: 0, approvals: 0 }
  );

  return (
    <div className="space-y-4" data-testid="brand-comparison">
      {/* Header */}
      <div className="rounded-sm border border-white/10 bg-black/50">
        <div className="px-5 py-3 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2 flex-wrap">
            <BarChart3 size={14} color={RED} /> MULTI-BRAND COMPARISON
            {cityFilter && (
              <span className="px-1.5 py-0.5 rounded-sm border text-[9.5px] font-extrabold tracking-[0.12em]"
                style={{ borderColor: RED, color: RED, background: "rgba(255,42,60,0.10)" }}
                data-testid="brand-city-filter">
                CITY: {cityFilter.toUpperCase()}
              </span>
            )}
          </p>
          <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">
            {totals.leads} leads · {fmt$(totals.revenue_cents)} revenue
          </span>
        </div>
      </div>

      {/* Three cards side-by-side (stack on mobile) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CARDS.map(({ brand, label, tagline, accent, Icon, future }) => {
          const r = rows.find((row) => row.brand === brand) || {
            leads: 0, assessments: 0, approvals: 0,
            revenue_cents: 0, conversion_pct: 0, revenue_per_lead: 0,
          };
          return (
            <BrandCard key={brand}
              brand={brand} label={label} tagline={tagline} accent={accent} Icon={Icon}
              data={r} future={future} totals={totals}
            />
          );
        })}
      </div>
    </div>
  );
}

function BrandCard({ brand, label, tagline, accent, Icon, data, future, totals }) {
  // Leads market share within this snapshot (excluding Future).
  const share = totals.leads > 0 ? Math.round((data.leads / totals.leads) * 100) : 0;
  return (
    <div
      className="rounded-sm border bg-black/50 overflow-hidden"
      style={{ borderColor: future ? "rgba(255,180,0,0.30)" : "rgba(255,255,255,0.10)" }}
      data-testid={`brand-card-${brand.toLowerCase()}`}
    >
      {/* Card header band */}
      <div className="px-5 py-4 border-b border-white/10 relative" style={{ background: `linear-gradient(180deg, ${accent}18 0%, transparent 100%)` }}>
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <CircleDot size={10} color={accent} className="animate-pulse" />
            <p className="font-heavy text-white text-[18px] tracking-tight" style={{ color: accent }}>{label}</p>
          </div>
          <Icon size={16} style={{ color: accent }} />
        </div>
        <p className="text-[10.5px] tracking-[0.14em] font-bold text-white/55 mt-1">{tagline}</p>
        {!future && totals.leads > 0 && (
          <div className="absolute top-3 right-12 text-[9.5px] tracking-[0.16em] font-extrabold text-white/45">
            {share}% share
          </div>
        )}
      </div>

      {future ? (
        <FutureBrandBody accent={accent} />
      ) : (
        <div className="px-5 py-5 space-y-3">
          {/* Headline metric: Revenue */}
          <div>
            <p className="text-[9.5px] tracking-[0.18em] font-bold text-white/45">REVENUE</p>
            <p className="font-heavy leading-none mt-1" style={{ color: NEON, fontSize: "30px" }}>
              {fmt$(data.revenue_cents)}
            </p>
          </div>
          <div className="h-px bg-white/5" />
          {/* 2x2 grid of secondary metrics */}
          <div className="grid grid-cols-2 gap-3">
            <Metric label="LEADS"        value={data.leads} Icon={Users} accent="white" />
            <Metric label="ASSESSMENTS"  value={data.assessments} Icon={CheckCircle2} accent="#5C8DFF" />
            <Metric label="APPROVALS"    value={data.approvals} Icon={Plug} accent={accent} />
            <Metric label="CONV %"       value={`${data.conversion_pct}%`} Icon={TrendingUp} accent={AMBER} />
          </div>
          <div className="h-px bg-white/5" />
          <div className="flex items-center justify-between">
            <p className="text-[9.5px] tracking-[0.16em] font-bold text-white/45">REV / LEAD</p>
            <p className="font-heavy text-white text-[18px]" style={{ color: NEON }}>${data.revenue_per_lead}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, Icon, accent }) {
  return (
    <div>
      <p className="text-[9.5px] tracking-[0.16em] font-bold text-white/45 inline-flex items-center gap-1">
        <Icon size={10} style={{ color: accent }} /> {label}
      </p>
      <p className="font-heavy text-white text-[20px] leading-tight mt-0.5">{value}</p>
    </div>
  );
}

function FutureBrandBody({ accent }) {
  return (
    <div className="px-5 py-8 text-center space-y-3">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm border-2 border-dashed" style={{ borderColor: accent, color: accent }}>
        <Sparkles size={20} />
      </div>
      <p className="text-[12px] text-white/65 leading-relaxed">
        Reserved slot for the next vertical.<br />
        Activate any time — Mission Control will surface it automatically.
      </p>
      <div className="inline-block px-3 py-1.5 rounded-sm border text-[10px] font-extrabold tracking-[0.18em]"
        style={{ borderColor: accent, color: accent, background: `${accent}10` }}>
        + NEW BRAND READY
      </div>
    </div>
  );
}
